import React, { useState } from 'react';
import { FiSave, FiX, FiPlus, FiTrash2, FiUpload } from 'react-icons/fi';
import QuestionsList from './QuestionsList';
import PackImageGallery from '../PackImageGallery';

export default function PackEditor({ pack, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    title: pack?.title || '',
    productId: pack?.productId || '',
    image: pack?.image || '',
    price: pack?.price || 'R$ 4,99',
    description: pack?.description || '',
    locked: pack?.locked ?? true,
    showInDiary: pack?.showInDiary ?? true,
    questions: pack?.questions || []
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function handleChange(field, value) {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  }

  function handleQuestionsChange(questions) {
    setFormData(prev => ({ ...prev, questions }));
  }

  function formatProductId(value) {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  async function handleSave() {
    if (!formData.title.trim()) {
      setError('O título é obrigatório');
      return;
    }

    if (!formData.productId.trim()) {
      setError('O Product ID é obrigatório');
      return;
    }

    const formattedProductId = formatProductId(formData.productId);
    const productIdAndroid = `product_${formattedProductId}`;
    const productIdIOS = `tai_product_${formattedProductId}`;

    setSaving(true);
    const success = await onSave({
      ...formData,
      productId: formattedProductId,
      productIdAndroid,
      productIdIOS,
      id: pack?.id
    });
    setSaving(false);

    if (!success) {
      setError('Erro ao salvar pack. Tente novamente.');
    }
  }

  return (
    <div style={styles.container}>
      {error && (
        <div style={styles.error}>
          {error}
        </div>
      )}

      <div style={styles.sections}>
        {/* Seção: Informações Básicas */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Informações do Pack</h2>
          
          <div style={styles.field}>
            <label style={styles.label}>Título *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Ex.: Reflexão"
              style={styles.input}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Product ID *</label>
            <input
              type="text"
              value={formData.productId}
              onChange={(e) => handleChange('productId', e.target.value)}
              placeholder="Ex.: apimentadas"
              style={styles.input}
            />
            <div style={styles.hint}>
              Android: product_{formatProductId(formData.productId || 'exemplo')}<br />
              iOS: tai_product_{formatProductId(formData.productId || 'exemplo')}
            </div>
          </div>

          <div style={styles.field}>
            <PackImageGallery
              currentImage={formData.image}
              onImageSelect={(url) => handleChange('image', url)}
              cardTitle="Imagem do Pack"
              cardHint="Galeria exclusiva para imagens de packs"
            />
          </div>

          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Preço</label>
              <input
                type="text"
                value={formData.price}
                onChange={(e) => handleChange('price', e.target.value)}
                placeholder="R$ 4,99"
                style={styles.input}
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>Status</label>
              <select
                value={formData.locked ? 'locked' : 'unlocked'}
                onChange={(e) => handleChange('locked', e.target.value === 'locked')}
                style={styles.input}
              >
                <option value="locked">Bloqueado</option>
                <option value="unlocked">Desbloqueado</option>
              </select>
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Diário de Reflexão</label>
            <select
              value={formData.showInDiary ? 'active' : 'inactive'}
              onChange={(e) => handleChange('showInDiary', e.target.value === 'active')}
              style={styles.input}
            >
              <option value="active">Ativo - Aparece no diário</option>
              <option value="inactive">Inativo - Não aparece no diário</option>
            </select>
            <div style={styles.hint}>
              {formData.showInDiary 
                ? '✓ Este pack aparecerá no diário de reflexão do app' 
                : '✗ Este pack não aparecerá no diário de reflexão do app'}
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Descrição</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              placeholder="Descrição do pack..."
              style={styles.textarea}
              rows={4}
            />
          </div>
        </section>

        {/* Seção: Perguntas */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>
            Perguntas ({formData.questions.length})
          </h2>
          <QuestionsList
            questions={formData.questions}
            onChange={handleQuestionsChange}
          />
        </section>
      </div>

      {/* Ações */}
      <div style={styles.actions}>
        <button
          onClick={onCancel}
          style={styles.btnCancel}
          disabled={saving}
        >
          <FiX size={18} /> Cancelar
        </button>
        <button
          onClick={handleSave}
          style={styles.btnSave}
          disabled={saving}
        >
          <FiSave size={18} /> {saving ? 'Salvando...' : 'Salvar Pack'}
        </button>
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
  error: {
    padding: 12,
    background: '#ffe8e6',
    border: '1px solid #ffcbc4',
    borderRadius: 10,
    color: '#9a3c2f',
    fontSize: 14,
    fontWeight: 600,
  },
  sections: {
    display: 'flex',
    flexDirection: 'column',
    gap: 24,
  },
  section: {
    background: '#fff',
    border: '1px solid #e8e9ef',
    borderRadius: 14,
    padding: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 700,
    margin: '0 0 20px 0',
    color: '#111',
  },
  field: {
    marginBottom: 16,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 16,
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#444',
    marginBottom: 6,
  },
  hint: {
    marginTop: 6,
    fontSize: 12,
    color: '#666',
    fontFamily: 'monospace',
    background: '#f5f6fb',
    padding: '8px 10px',
    borderRadius: 6,
    border: '1px solid #e8e9ef',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d9dbe7',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d9dbe7',
    borderRadius: 10,
    fontSize: 14,
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  },
  imagePreview: {
    marginTop: 12,
    maxWidth: 200,
    border: '1px solid #e8e9ef',
    borderRadius: 10,
    overflow: 'hidden',
  },
  previewImg: {
    width: '100%',
    display: 'block',
  },
  actions: {
    display: 'flex',
    gap: 12,
    justifyContent: 'flex-end',
    paddingTop: 8,
  },
  btnCancel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 20px',
    background: '#f5f6fb',
    color: '#111',
    border: '1px solid #d9dbe7',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnSave: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '12px 20px',
    background: '#1f6feb',
    color: '#fff',
    border: 'none',
    borderRadius: 10,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
};
